import { Image as ImageIcon, X, AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { LoadingSpinner } from "@/components/ui/LoadingSpinner"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import {
  useImageUrl,
  type EventGalleryImage,
} from "@/hooks/useEventGallery"
import { formatFileSize } from "@/lib/utils/formatFileSize"

interface ImagePreviewModalProps {
  image: EventGalleryImage
  onClose: () => void
}

// Enhanced Image preview modal component with better authentication handling
export const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ image, onClose }) => {
  const { data: imageUrl, isLoading, error } = useImageUrl(image.file_path)

  console.log('ImagePreviewModal render:', {
    imageName: image.name,
    filePath: image.file_path,
    imageUrl,
    isLoading,
    error
  })

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>{image.name}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          {isLoading ? (
            <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
              <LoadingSpinner size="large" iconClassName="text-gray-400" label="Loading image..." />
            </div>
          ) : error ? (
            <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
              <div className="text-center">
                {error.message?.includes('Authentication required') ? (
                  <>
                    <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <span className="text-gray-700 mb-2">Authentication required</span>
                    <p className="text-sm text-gray-500">Please log in to view this image</p>
                  </>
                ) : (
                  <>
                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <span className="text-gray-500">Failed to load image</span>
                    <p className="text-xs text-red-500 mt-2">{error.message}</p>
                  </>
                )}
              </div>
            </div>
          ) : imageUrl ? (
            <ImageWithFallback
              src={imageUrl}
              alt={image.name}
              className="w-full max-h-96 object-contain rounded-lg"
              fallbackClassName="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center"
              onError={() => console.error('ImagePreviewModal ImageWithFallback onError called')}
            />
          ) : (
            <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
              <div className="text-center">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <span className="text-gray-500">No image URL available</span>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">File Size:</span>{" "}
              {image.file_size ? formatFileSize(image.file_size) : "N/A"}
            </div>
            <div>
              <span className="font-medium">Type:</span>{" "}
              {image.mime_type || "N/A"}
            </div>
            {image.width && image.height && (
              <div>
                <span className="font-medium">Dimensions:</span>{" "}
                {image.width} × {image.height}
              </div>
            )}
            <div>
              <span className="font-medium">Uploaded:</span>{" "}
              {new Date(image.created_at).toLocaleDateString()}
            </div>
            <div className="col-span-2">
              <span className="font-medium">File Path:</span>{" "}
              <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{image.file_path}</code>
            </div>
            {imageUrl && (
              <div className="col-span-2">
                <span className="font-medium">Signed URL:</span>{" "}
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded break-all">{imageUrl.substring(0, 100)}...</code>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
